import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, Subject, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { API_BASE } from '../../constants/general.constants';
import {
  GenerateStepRequest,
  JobAnalysisResponse,
  LlmAvailableModel,
  LlmChatMessage,
  LlmChatRequest,
  LlmChatResponse,
  LlmStatusResponse,
  LlmStreamEvent,
  SummarizeErrorRequest,
  SummarizeErrorResponse,
} from '../../interfaces/llm.interface';

@Injectable({ providedIn: 'root' })
export class LlmService {
  readonly #http = inject(HttpClient);
  readonly #oauth = inject(OAuthService);

  // ── Operational LLM status ──────────────────────────────────────────────

  /**
   * Whether LLM features should be offered for the current context.
   * Uses {@link getProjectLlmStatus} when `projectId` is set (no admin role required).
   * Without `projectId`, returns disabled — avoids `GET /admin/llm/operational`, which
   * requires platform admin and responds 403 for normal users.
   */
  getOperationalStatus(projectId?: number | null): Observable<LlmStatusResponse> {
    if (projectId != null && projectId > 0) {
      return this.getProjectLlmStatus(projectId);
    }
    return of({ configured: false, enabled: false });
  }

  /**
   * Check if LLM is available for a specific project by probing the models endpoint.
   * Returns { configured: true, enabled: true } on success, false values on error/404.
   */
  getProjectLlmStatus(projectId: number): Observable<LlmStatusResponse> {
    return this.#http.get<{ models: unknown[] }>(
      `${API_BASE}/project/${projectId}/llm/models`
    ).pipe(
      map(() => ({ configured: true, enabled: true } as LlmStatusResponse)),
      catchError(() => of({ configured: false, enabled: false } as LlmStatusResponse))
    );
  }

  /**
   * List available LLM models for a project. Any HTTP error yields an empty
   * list, which is what the UI callers (editor completions) want.
   */
  listModels(projectId: number): Observable<LlmAvailableModel[]> {
    return this.listModelsOrThrow(projectId).pipe(
      catchError(() => of([]))
    );
  }

  /**
   * List available LLM models for a project, letting HTTP errors propagate.
   * Used by the step runtime's EHQ.listLLMModels, which must throw on errors
   * like enoch does rather than return an empty list (EHQ-386).
   */
  listModelsOrThrow(projectId: number): Observable<LlmAvailableModel[]> {
    return this.#http.get<{ models: LlmAvailableModel[] }>(
      `${API_BASE}/project/${projectId}/llm/models`
    ).pipe(
      map(resp => resp?.models ?? [])
    );
  }

  // ── Job Analysis (project-scoped) ─────────────────────────────────────

  analyzeJob(projectId: number, jobId: number): Observable<JobAnalysisResponse> {
    return this.#http.post<JobAnalysisResponse>(
      `${API_BASE}/project/${projectId}/llm/analyze-job/${jobId}`,
      {}
    );
  }

  // ── Operational Summarize Error ────────────────────────────────────────

  summarizeError(request: SummarizeErrorRequest): Observable<SummarizeErrorResponse> {
    return this.#http.post<SummarizeErrorResponse>(
      `${API_BASE}/llm/operational/summarize-error`,
      request
    );
  }

  // ── Streaming Chat (operational) ──────────────────────────────────────

  /**
   * Stream a chat completion via SSE using the operational LLM.
   * Uses raw fetch() because Angular HttpClient doesn't support SSE streaming.
   * Returns a Subject that emits parsed SSE events and completes on the final chunk.
   */
  streamOperationalChat(
    messages: LlmChatMessage[],
    options?: {
      promptName?: string;
      context?: Record<string, unknown>;
      system?: string;
      model?: string;
      maxTokens?: number;
      temperature?: number;
    }
  ): { events$: Subject<LlmStreamEvent>; abort: () => void } {
    const events$ = new Subject<LlmStreamEvent>();
    const controller = new AbortController();

    const body: LlmChatRequest = {
      messages,
      stream: true,
      ...options,
    };

    this.#fetchSSE(
      `${API_BASE}/llm/operational/chat`,
      body,
      controller.signal,
      events$
    );

    return {
      events$,
      abort: () => controller.abort(),
    };
  }

  /**
   * Non-streaming chat completion via the operational LLM.
   */
  operationalChat(
    messages: LlmChatMessage[],
    options?: {
      promptName?: string;
      context?: Record<string, unknown>;
      system?: string;
      model?: string;
      maxTokens?: number;
      temperature?: number;
    }
  ): Observable<LlmChatResponse> {
    const body: LlmChatRequest = {
      messages,
      stream: false,
      ...options,
    };
    return this.#http.post<LlmChatResponse>(
      `${API_BASE}/llm/operational/chat`,
      body
    );
  }

  // ── Streaming Chat (project-scoped) ───────────────────────────────────

  streamProjectChat(
    projectId: number,
    messages: LlmChatMessage[],
    options?: {
      promptName?: string;
      context?: Record<string, unknown>;
      system?: string;
      model?: string;
      maxTokens?: number;
      temperature?: number;
    }
  ): { events$: Subject<LlmStreamEvent>; abort: () => void } {
    const events$ = new Subject<LlmStreamEvent>();
    const controller = new AbortController();

    const body: LlmChatRequest = {
      messages,
      stream: true,
      ...options,
    };

    this.#fetchSSE(
      `${API_BASE}/project/${projectId}/llm/chat`,
      body,
      controller.signal,
      events$
    );

    return {
      events$,
      abort: () => controller.abort(),
    };
  }

  // ── Generate Step (project-scoped, streaming) ────────────────────────

  generateStep(
    projectId: number,
    body: GenerateStepRequest
  ): { events$: Subject<LlmStreamEvent>; abort: () => void } {
    const events$ = new Subject<LlmStreamEvent>();
    const controller = new AbortController();

    this.#fetchSSEGeneric(
      `${API_BASE}/project/${projectId}/llm/generate-step`,
      body,
      controller.signal,
      events$
    );

    return {
      events$,
      abort: () => controller.abort(),
    };
  }

  // ── SSE fetch implementation ──────────────────────────────────────────

  async #fetchSSE(
    url: string,
    body: LlmChatRequest,
    signal: AbortSignal,
    subject: Subject<LlmStreamEvent>
  ): Promise<void> {
    return this.#fetchSSEGeneric(url, body, signal, subject);
  }

  async #fetchSSEGeneric(
    url: string,
    body: object,
    signal: AbortSignal,
    subject: Subject<LlmStreamEvent>
  ): Promise<void> {
    try {
      const reader = await this.#openSSEStream(url, body, signal, subject);
      if (!reader) return;

      await this.#readSSEStream(reader, subject);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        subject.complete();
      } else {
        subject.error(err);
      }
    }
  }

  async #openSSEStream(
    url: string,
    body: object,
    signal: AbortSignal,
    subject: Subject<LlmStreamEvent>
  ): Promise<ReadableStreamDefaultReader<Uint8Array> | null> {
    const token = this.#oauth.getAccessToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      subject.error(new Error(errorText || `HTTP ${response.status}`));
      return null;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      subject.error(new Error('No response body'));
      return null;
    }
    return reader;
  }

  async #readSSEStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    subject: Subject<LlmStreamEvent>
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed?.startsWith('data: ')) continue;

        const jsonStr = trimmed.slice(6);
        try {
          const event = JSON.parse(jsonStr) as LlmStreamEvent;
          subject.next(event);
          if (event.done) {
            subject.complete();
            return;
          }
        } catch {
          // Skip malformed JSON lines
        }
      }
    }

    subject.complete();
  }
}
